import { Link } from 'react-router-dom'
import { Explicacao } from '../../../components/Explicacao'
import { TEXTOS } from '../../../lib/glossario'
import type { GrupoSuspeito, SuspeitasDeFracionamento as Suspeitas } from '../../../types/chamado'
import { enderecoDoChamado } from './indicadores'

/**
 * Os chamados fechados do mês que COINCIDEM em órgão, categoria e semana de abertura.
 *
 * **Isto não é texto de máquina: é uma consulta.** Por isso não leva selo de procedência de IA — o
 * agrupamento é um `GROUP BY`, determinístico e reproduzível, e carimbá-lo de "leitura automática"
 * ensinaria a desconfiar de um fato exato.
 *
 * **E é coincidência, nunca conclusão.** Pelo contrato, chamados de mesma causa inicial contam como
 * UM (ANEXO IV, definição 2, p.89) — fracionar um problema em vários chamados infla o denominador
 * dos seis indicadores e melhora todos os percentuais sem que nada tenha melhorado. Mas categoria
 * igual não é causa igual, e nesta base o título nem separa (13 valores distintos em 477 chamados,
 * rótulos de um menu). Quem diz se a causa é a mesma é o fiscal, abrindo os chamados. Por isso o
 * texto fala em "coincidem" e a tela não oferece botão nenhum de agrupar: a reclassificação
 * continua sendo ato humano, chamado a chamado, registrado com o nome de quem o fez.
 *
 * @param props os grupos apurados no fechamento
 * @returns o cartão das coincidências do mês
 */
export function SuspeitasDeFracionamento({ suspeitas }: { suspeitas: Suspeitas }) {
  const t = TEXTOS.suspeitasDeFracionamento

  return (
    <section className="cartao p-5" aria-label={t.titulo}>
      <h2 className="flex items-center gap-1.5 text-base font-semibold text-slate-900">
        {t.titulo}
        {/* "Chamado-pai" é termo do contrato e não se traduz — ganha ajuda no lugar de tradução. */}
        <Explicacao titulo="Chamado-pai" texto={t.ajudaChamadoPai} />
      </h2>
      <p className="mt-1 max-w-prose text-sm text-slate-500">{t.ajuda}</p>

      {suspeitas.detalhe.length === 0 ? (
        <p className="mt-3 text-sm text-slate-600">{t.vazio}</p>
      ) : (
        <>
          <ul className="mt-3 space-y-3">
            {suspeitas.detalhe.map((grupo) => (
              <Grupo key={chave(grupo)} grupo={grupo} />
            ))}
          </ul>
          <p className="mt-4 max-w-prose text-sm text-slate-500">{t.naoAgrupa}</p>
        </>
      )}
    </section>
  )
}

/** Órgão, categoria e semana identificam o grupo — são exatamente as colunas que o formaram. */
function chave(grupo: GrupoSuspeito): string {
  return `${grupo.entidade}|${grupo.categoria}|${grupo.semana}`
}

/**
 * Um grupo, com os números abrindo a listagem já filtrada.
 *
 * Cada número é um link para o chamado, e não uma etiqueta: o fiscal só consegue dizer se a causa é
 * a mesma lendo os chamados, e a distância entre a suspeita e a conferência tem de ser um clique.
 *
 * @param props o grupo apurado
 * @returns a linha do grupo
 */
function Grupo({ grupo }: { grupo: GrupoSuspeito }) {
  const t = TEXTOS.suspeitasDeFracionamento

  return (
    <li className="rounded-md border border-slate-200 p-3">
      <p className="text-sm font-medium text-slate-900">{grupo.entidade}</p>
      <p className="mt-0.5 text-sm text-slate-600">{grupo.categoria}</p>
      <p className="mt-0.5 text-xs text-slate-500">
        {t.semana(grupo.semana)} · {t.grupo(grupo.chamados.length)}
      </p>
      <ul className="mt-2 flex flex-wrap gap-x-3 gap-y-1">
        {grupo.chamados.map((numero) => (
          <li key={numero}>
            <Link
              to={enderecoDoChamado(numero)}
              className="text-sm tabular-nums text-niteroi-700 underline underline-offset-2 hover:text-niteroi-900"
            >
              {numero}
            </Link>
          </li>
        ))}
      </ul>
    </li>
  )
}
